import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getGetEnginesIdQueryKey,
  getGetEnginesMyQueryKey,
} from '@/types/origin-point-engine'
import { showErrorData, showSuccessData } from '@/lib/toast'
import { useEditorStore } from '@/features/editor/stores/editor-store'
import { postEngineReviewAction } from '@/features/editor/lib/editor-review.api'
import { validateEditorSave } from '@/features/editor/lib/editor-validate'

const REVIEW_CONFLICT_ERROR = '已有待审核版本，请先撤回'

function isReviewConflict(err: unknown): boolean {
  const e = err as {
    response?: { status?: number; data?: { error?: string } }
  }
  const status = e?.response?.status
  const message = e?.response?.data?.error
  return status === 400 && message === REVIEW_CONFLICT_ERROR
}

export function useSubmitReview() {
  const queryClient = useQueryClient()

  const engineId = useEditorStore((s) => s.draft.engineId)
  const setPublishing = useEditorStore((s) => s.setPublishing)
  const setUnpublishing = useEditorStore((s) => s.setUnpublishing)

  const [conflictOpen, setConflictOpen] = useState(false)

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!engineId) {
        throw new Error('Missing engineId')
      }
      return postEngineReviewAction({ engineId, action: 'submit' })
    },
    onMutate: () => {
      setPublishing(true)
    },
    onSuccess: async () => {
      showSuccessData('已提交审核')
      if (engineId) {
        await queryClient.invalidateQueries({
          queryKey: getGetEnginesIdQueryKey(engineId),
        })
      }
      await queryClient.invalidateQueries({
        queryKey: getGetEnginesMyQueryKey(),
      })
    },
    onError: (err) => {
      if (isReviewConflict(err)) {
        setConflictOpen(true)
        return
      }
      showErrorData('提交审核失败')
    },
    onSettled: () => {
      setPublishing(false)
    },
  })

  const withdrawMutation = useMutation({
    mutationFn: async () => {
      if (!engineId) {
        throw new Error('Missing engineId')
      }
      return postEngineReviewAction({ engineId, action: 'withdraw' })
    },
    onMutate: () => {
      setUnpublishing(true)
    },
    onSuccess: async () => {
      if (engineId) {
        await queryClient.invalidateQueries({
          queryKey: getGetEnginesIdQueryKey(engineId),
        })
      }
      await queryClient.invalidateQueries({
        queryKey: getGetEnginesMyQueryKey(),
      })
    },
    onError: () => {
      showErrorData('撤回审核失败')
    },
    onSettled: () => {
      setUnpublishing(false)
    },
  })

  function submitReview() {
    if (!engineId) {
      showErrorData('无法提交审核', '缺少 engineId')
      return
    }

    const st = useEditorStore.getState()
    const validationError = validateEditorSave(st.draft, st.knowledge)
    if (validationError) {
      showErrorData('无法提交审核', validationError)
      return
    }

    submitMutation.mutate()
  }

  function withdrawReview(opts?: { onSuccess?: () => void }) {
    if (!engineId) {
      showErrorData('无法撤回审核', '缺少 engineId')
      return
    }

    withdrawMutation.mutate(undefined, {
      onSuccess: () => {
        opts?.onSuccess?.()
      },
    })
  }

  function closeConflict() {
    setConflictOpen(false)
  }

  return {
    submitReview,
    withdrawReview,
    closeConflict,
    conflictOpen,
    isSubmitting: submitMutation.isPending,
    isWithdrawing: withdrawMutation.isPending,
  }
}
